import { useCallback, useEffect, useRef } from 'react';

import { analytics } from '../core/analytics';

/**
 * Emits sessionStarted once on mount and sessionEnded when the tab is closed
 * (pagehide). Tab visibility changes and back-forward-cache hides do not
 * emit sessionEnded or pageLeft. A persisted restore after a real end starts
 * a new session so the next end is paired with a start and a fresh duration.
 */
export function useSessionTracking(): void {
    const sessionStartedRef = useRef(false);
    const sessionEndedRef = useRef(false);
    const sessionStartTimeRef = useRef<number>(Date.now());

    const emitSessionStarted = useCallback((): void => {
        if (sessionStartedRef.current) {
            return;
        }

        sessionStartedRef.current = true;
        analytics.sessionStarted({
            deviceType: getDeviceType(),
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        });
    }, []);

    useEffect(() => {
        sessionStartTimeRef.current = Date.now();
        emitSessionStarted();
    }, [emitSessionStarted]);

    useEffect(() => {
        const emitSessionEnded = (event: Event): void => {
            if (isPersistedTransition(event) || sessionEndedRef.current) {
                return;
            }

            if (!sessionStartedRef.current) {
                sessionStartTimeRef.current = Date.now();
                emitSessionStarted();
            }

            sessionEndedRef.current = true;

            analytics.pageLeft({ currentUrl: window.location.href });
            analytics.sessionEnded({
                sessionDurationMs: Date.now() - sessionStartTimeRef.current,
            });
        };

        const restoreSessionIfPersisted = (event: Event): void => {
            if (!isPersistedTransition(event) || !sessionEndedRef.current) {
                return;
            }

            sessionEndedRef.current = false;
            sessionStartedRef.current = false;
            sessionStartTimeRef.current = Date.now();
            emitSessionStarted();
        };

        window.addEventListener('pagehide', emitSessionEnded);
        window.addEventListener('pageshow', restoreSessionIfPersisted);

        return () => {
            window.removeEventListener('pagehide', emitSessionEnded);
            window.removeEventListener('pageshow', restoreSessionIfPersisted);
        };
    }, [emitSessionStarted]);
}

function isPersistedTransition(event: Event): boolean {
    return 'persisted' in event && event.persisted === true;
}

function getDeviceType(): string {
    const width = window.innerWidth;

    if (width < 768) {
        return 'mobile';
    }

    if (width < 1024) {
        return 'tablet';
    }

    return 'desktop';
}
